import { useState } from "react";
import { Phone, HelpCircle, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const faqs = [
  { q: "How long does it take to complete a custom sign?", a: "Simple signs such as vinyl prints and window graphics are typically completed within 2-5 business days. Larger projects like channel letters require approximately 2 weeks. Please note: production begins only after design approval, and the design process starts once the initial deposit has been received." },
  { q: "Do you offer free estimates?", a: "Absolutely! We offer completely free estimates and free on-site surveys at no obligation. Our team will visit your location to assess your signage needs and provide a detailed quote." },
  { q: "What areas do you serve?", a: "We proudly serve all of Greater Vancouver including Vancouver, Burnaby, Richmond, Surrey, Coquitlam, North & West Vancouver, Langley, and the broader Lower Mainland. We've also completed projects in Kelowna, Merritt, Victoria, and Nanaimo." },
  { q: "Is installation included in the price?", a: "Installation and delivery pricing depends on the complexity of the installation and the distance to your location. In select cases, we offer complimentary installation and delivery as part of promotional packages." },
  { q: "What warranty do you provide?", a: "All of our signs come with a comprehensive warranty that can be extended up to 10 years for an additional fee. We also provide 10 years of dedicated customer service support to ensure your signage continues to perform at its best." },
  { q: "How much does a custom sign cost?", a: "LED displays start from $290. Window graphics, vinyl prints, and non-illuminated composite signs start from approximately $200. Every project is unique. contact us for a free personalized quote tailored to your specific requirements." },
  { q: "Can you help with sign permits?", a: "Yes! Permit assistance is one of our standout services. We handle the entire permitting process on your behalf, navigating municipal bylaws and regulations. We also offer permit services for other sign companies." },
  { q: "Do you offer maintenance & repairs?", a: "Yes. we have a registered Red Seal certified electrician on our team. We provide comprehensive sign repair, LED module replacement, electrical services, and maintenance for all types of signage, including signs installed by other companies." },
  { q: "What is your fabrication and delivery policy?", a: "A 50% deposit is required to begin fabrication once the quotation is approved. Once complete, you may visit our shop to review and pick up your sign, or we'll send photos for your approval. After sign-off, installation is scheduled and the remaining balance is due at the time of installation." },
];

export default function FloatingCallBtn() {
  const [faqOpen, setFaqOpen] = useState(false);

  return (
    <>
      {/* FAQ Panel */}
      <AnimatePresence>
        {faqOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="fixed bottom-28 right-4 left-4 sm:left-auto sm:right-6 z-50 sm:w-[360px] max-h-[70vh] bg-[#131a23] border border-white/10 rounded-2xl shadow-2xl shadow-black/50 overflow-hidden flex flex-col"
            data-testid="faq-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/8">
              <div className="flex items-center gap-2">
                <HelpCircle className="w-4 h-4 text-gold" />
                <h3 className="font-heading font-bold text-white text-sm">Frequently Asked Questions</h3>
              </div>
              <button
                onClick={() => setFaqOpen(false)}
                data-testid="faq-close-btn"
                className="w-7 h-7 rounded-full bg-white/5 flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 transition-all"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            {/* Content */}
            <div className="overflow-y-auto flex-1 px-5 py-3 custom-scrollbar">
              <Accordion type="single" collapsible className="space-y-1.5">
                {faqs.map((faq, i) => (
                  <AccordionItem
                    key={i}
                    value={`faq-${i}`}
                    data-testid={`faq-item-${i}`}
                    className="border-b border-white/5 last:border-none"
                  >
                    <AccordionTrigger
                      className="text-white text-left font-medium text-xs hover:no-underline hover:text-gold py-3 [&>svg]:text-gold [&>svg]:w-3.5 [&>svg]:h-3.5"
                      data-testid={`faq-trigger-${i}`}
                    >
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-white/50 text-xs leading-relaxed pb-3">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Buttons */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-3 items-center">
        {/* FAQ Button */}
        <button
          onClick={() => setFaqOpen(!faqOpen)}
          data-testid="floating-faq-btn"
          className={`w-14 h-14 rounded-full flex items-center justify-center transition-all duration-300 hover:-translate-y-0.5 ${
            faqOpen
              ? "bg-white/10 border border-white/20 text-white shadow-lg"
              : "bg-[#1a2332] border border-white/10 text-white/70 hover:text-gold hover:border-gold/30 shadow-[0_0_15px_rgba(0,0,0,0.4)]"
          }`}
          aria-label="FAQ"
        >
          {faqOpen ? <X className="w-5 h-5" /> : <HelpCircle className="w-5 h-5" />}
        </button>

        {/* Call Button */}
        <a
          href="tel:+17783220801"
          data-testid="floating-call-btn"
          className="w-14 h-14 rounded-full bg-gold text-black flex items-center justify-center shadow-[0_0_20px_rgba(255,191,0,0.3)] hover:shadow-[0_0_30px_rgba(255,191,0,0.5)] hover:-translate-y-0.5 transition-all duration-300"
          aria-label="Call VANSIGN"
        >
          <Phone className="w-5 h-5" />
        </a>
      </div>
    </>
  );
}
